import React, {useMemo, useState} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {Box, IconButton, Paper, Tab, Tabs, Tooltip, LinearProgress, useTheme} from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import {DataGridPro} from '@mui/x-data-grid-pro';
import Canvas from '../../navigation/canvas/Canvas';
import ErrorPopup from '../../common/ErrorPopup';
import MessagePopup from '../../common/MessagePopup';
import ProviderEditDialog from './ProviderEditDialog';
import ScrollableDialog from '../../common/ScrollableDialog';
import TableToolbar from '../../common/TableToolbar';
import ProviderService from '../../../apis/ProviderService';
import {columns} from './columns';
import IyoGridToolbar from '../../common/IyoGridToolbar';
import useLinkedRow from '../../common/useLinkedRow';
import CategoryTab from './CategoryTab';
import ProviderCategoryService from '../../../apis/ProviderCategoryService';
import ProviderChildCategoryService from '../../../apis/ProviderChildCategoryService';

const TAB_PROVIDERS = 0;
const TAB_CATEGORY = 1;
const TAB_SUB_CATEGORY = 2;

const Providers = ({}) => {
  // ####################[ Setup and Initialization ]####################
  const queryClient = useQueryClient();

  const initializeColumns = (initialColumns) => {
    return initialColumns.concat({
      field: 'actions',
      headerName: 'Actions',
      description: 'Actions',
      minWidth: 175,
      renderCell: (params) => {
        return (
          <>
            <Tooltip title='View Schema'>
              <IconButton color='primary' onClick={(event) => handleDialogOpen(event, true, params.row)}>
                <InfoIcon/>
              </IconButton>
            </Tooltip>
            &nbsp;
            <Tooltip title='View Properties'>
              <IconButton color='primary' onClick={(event) => handleDialogOpen(event, false, params.row)}>
                <BuildCircleIcon/>
              </IconButton>
            </Tooltip>
            &nbsp;
            <Tooltip title='Edit'>
              <IconButton color='primary' onClick={(event) => handleEdit(event, params.row, queryClient, setState)}>
                <EditIcon/>
              </IconButton>
            </Tooltip>
          </>
        );
      }
    });
  };

  // ####################[ State Definitions ]####################
  const [tab, setTab] = useState(TAB_PROVIDERS);
  const [cols, setCols] = useState(initializeColumns(columns));
  const [dialogContent, setDialogContent] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [editMode, setEditMode] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [errDisplayText, setErrDisplayText] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [providerId, setProviderId] = useState(null);
  const [rowEditing, setRowEditing] = useState([]);

  const state = { editMode, editOpen, rowEditing, providerId };
  const setState = { setEditMode, setEditOpen, setErrorOpen, setErrDisplayText, setMsgOpen, setMsgText,
    setProviderId, setRowEditing };

  const formatData = (dataArray) => {
    let returnArray = [];
    for (let data of dataArray) {
      let rec = Object.assign({}, data);


      for (let field in rec)
        if (rec[field] === null)
          rec[field] = '';

      const boolFields = ['has_hwm', 'device_provider', 'timezone_sensitive', 'v3', 'multi_tenant_enabled'];
      for (let field of boolFields) {
        if (rec[field] === 1)
          rec[field] = 'true';
        else if (rec[field] === 0)
          rec[field] = 'false';
      }

      const jsonFields = ['schema', 'properties'];
      for (let field of jsonFields)
        rec[field] = JSON.stringify(rec[field], null, 2);

      returnArray.push(rec);
    }
    return returnArray;
  };

  // ####################[ Query Definitions ]####################
  const getProviders = useQuery('getProvidersPage', async () => {
      return formatData(await ProviderService.getAll());
    },
    {
      onError: (error) => {
        handleError(error);
      },
      retry: false,
      staleTime: Infinity
    }
  );

  // Kept as its own query rather than folded into getProvidersPage: the providers grid must
  // still render if this fails, and sharing the key means CategoryEditDialog's invalidation
  // refreshes the Sub-Category column below without a manual refresh.
  const getChildCategories = useQuery('getProviderChildCategoriesPage',
    async () => ProviderChildCategoryService.getAll(),
    {onError: (error) => handleError(error), retry: false, staleTime: Infinity}
  );
  const childNameById = useMemo(
    () => new Map((getChildCategories.data || []).map((cat) => [cat.id, cat.name])),
    [getChildCategories.data]
  );
  const rows = useMemo(
    () => (getProviders.data || []).map((row) => ({
      ...row,
      provider_child_category_name: childNameById.get(row.provider_child_category_id) ?? ''
    })),
    [getProviders.data, childNameById]
  );

  const getCategories = useQuery('getProviderCategoriesPage',
    async () => ProviderCategoryService.getAll(),
    {onError: (error) => handleError(error), retry: false, staleTime: Infinity}
  );
  const categoryOptions = (getCategories.data || [])
    .filter((cat) => cat.active)
    .map((cat) => ({name: cat.name, value: cat.id}))
    .sort((a, b) => a.name.localeCompare(b.name));

  // ####################[ Event Handlers ]####################
  const handleCreate = async () => {
    setState.setEditMode('create');
    setState.setRowEditing(null);
    setState.setEditOpen(true);
  };

  const handleDialogOpen = async (event, isSchema, row) => {
    if (isSchema) {
      setDialogContent(row.schema);
      setDialogTitle('JSON Schema');
    }
    else {
      setDialogContent(row.properties);
      setDialogTitle('Properties');
    }
    setDialogOpen(true);
  };

  const handleDialogClose = async () => {
    setDialogOpen(false);
  };

  const handleEdit = async (event, row) => {
    setEditMode('edit');
    setRowEditing(row);
    setProviderId(row.id);
    setEditOpen(true);
  };

  // Arriving from the home page search as /providers?id=5 opens that provider.
  useLinkedRow(getProviders.data, (row) => handleEdit(null, row));

  const handleError = (error) => {
    setErrorOpen(false);
    setErrDisplayText(`${error.message}` +
      `${error.response?.data ? ': ' + JSON.stringify(error.response.data) : ''}`);
    setErrorOpen(true);

    // axios gives no `response` at all for a network/CORS failure, so dereferencing it here
    // threw a TypeError that replaced the real error with a crash.
    console.error(`${error.message}: ${JSON.stringify(error.response?.data)}`);
  };

  const refreshTable = async () => {
    await queryClient.invalidateQueries('getProvidersPage');
  };

  // ####################[ The Component ]####################
  return (
    <Box>
      <Canvas appName='nGEST' title='Providers'>
        <Tabs value={tab} onChange={(event, value) => setTab(value)} sx={{mb: '1rem'}}>
          <Tab label='Providers'/>
          <Tab label='Category'/>
          <Tab label='Sub-Category'/>
        </Tabs>
        {tab === TAB_PROVIDERS && <>
        <TableToolbar
          editHandler={() => handleCreate(setState)}
          onRefresh={refreshTable}
        />
        <Paper sx={{height: '77vh', '& .disabled': {color: '#94A3B8'}}}>
          <DataGridPro
            columns={cols}
            rows={rows}
            getRowClassName={(params) => params.row.disabled ? 'disabled' : ''}
            slots={{loadingOverlay: LinearProgress, toolbar: IyoGridToolbar}}
            loading={getProviders.isLoading}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  description: false,
                  created_by: false,
                  created_datetime: false,
                  updated_by: false,
                  updated_datetime: false,
                  timezone_sensitive: false,
                  cron: false,
                  concurrency_limit: false,
                  backoff_interval: false,
                  has_hwm: false
                }
              }
            }}
            density='compact'
            sx={{
              '& .run-params-header': {backgroundColor: '#ECFDF5'},
              '& .bool-cell-true': {color: 'darkgreen'},
              '& .bool-cell-false': {color: 'darkred'}
            }}
          />
        </Paper>
        </>}
        {tab === TAB_CATEGORY && (
          <CategoryTab
            entityLabel='Category'
            queryKey='getProviderCategoriesPage'
            service={ProviderCategoryService}
          />
        )}
        {tab === TAB_SUB_CATEGORY && (
          <CategoryTab
            entityLabel='Sub-Category'
            queryKey='getProviderChildCategoriesPage'
            service={ProviderChildCategoryService}
            parentOptions={categoryOptions}
          />
        )}
      </Canvas>
      <ProviderEditDialog
        state={state}
        setState={setState}
      />
      <ScrollableDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        close={handleDialogClose}
        content={dialogContent}
        title={dialogTitle}
        scroll={'paper'}
      />
      <MessagePopup
        open={msgOpen}
        setOpen={setMsgOpen}
        displayText={msgText}
      />
      <ErrorPopup
        open={errorOpen}
        setOpen={setErrorOpen}
        displayText={errDisplayText}
      />
    </Box>
  );
};

export default Providers;